import { useMemo, useReducer, useState } from 'react';
import styled from 'styled-components';
import _, { identity } from 'lodash';
import { Message } from '../basics/Message';
import YearlyMetricsChart from './YearlyMetricsChart';
import MetricSelect from './MetricSelect';
import Endash from '../basics/Endash';
import { useYearsArray } from '../../utils/hooks';
import ExportYearlyDataButton from './ExportYearlyDataButton';

const ChartDiv = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: hidden; /* Fix for recharts animation on resize */
  padding: 15px 0;
`;

const SummaryDiv = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 40vh;
  height: 30vw;
`;

const selectedMetricsReducer = (state, action) => {
  const newState = [...state];
  newState[action.index] = action.value;
  return newState;
};

const SelectableMetricChart = ({
  metrics = [],
  data = {},
  arrayTransform /* A transform or filter function for the aggregate metrics */,
  msg,
  className,
}) => {
  const [
    selectedMetrics,
    dispatchSelectedMetrics,
  ] = useReducer(selectedMetricsReducer, [
    metrics[0].accessor,
    metrics[1].accessor,
  ]);

  const [hoveredData, setHoveredData] = useState({});

  const onChartMouseMove = (hovered) => {
    setHoveredData(hovered?.activePayload?.[0]?.payload || {});
  };

  const years = useYearsArray();

  const ticks = useMemo(
    () => [
      years[0],
      // Don't show the final tick if we are showing a hovered tick,
      // because it will sometimes hide the hovered tick.
      hoveredData.year != null ? hoveredData.year : years[years.length - 1],
    ],
    [years, hoveredData],
  );

  const metricsByAccessor = useMemo(() => _.keyBy(metrics, 'accessor'), [
    metrics,
  ]);

  const metricOptions = useMemo(
    () =>
      metrics.map(({ accessor, displayName }) => ({
        key: accessor,
        value: accessor,
        name: displayName,
        disabled: selectedMetrics.includes(accessor),
      })),
    [metrics, selectedMetrics],
  );

  const chartMetrics = metrics.map((m) => ({
    ...m,
    shown: selectedMetrics.includes(m.accessor),
  }));

  return (
    <SummaryDiv className={className}>
      <div className="d-flex justify-content-between align-items-start">
        <div className="d-flex">
          {selectedMetrics.map((accessor, index) => {
            const {
              displayName,
              color,
              aggregator,
              transform = identity,
              format,
              info,
            } = metricsByAccessor[accessor];
            const currentValue =
              hoveredData[accessor] != null ? (
                format(hoveredData[accessor])
              ) : data[accessor] != null ? (
                format(transform(aggregator(arrayTransform(data[accessor]))))
              ) : (
                <span className="text-muted">
                  <Endash />
                </span>
              );
            return (
              <MetricSelect
                key={accessor}
                className="mr-4"
                color={color}
                options={metricOptions}
                selectedOption={displayName}
                value={currentValue}
                // When the metric is only for a single year, don't show aggregation info
                info={hoveredData[accessor] != null ? undefined : info}
                setOption={({ value }) =>
                  dispatchSelectedMetrics({ index, value })
                }
              />
            );
          })}
        </div>
        {!msg && (
          <ExportYearlyDataButton
            data={data}
            cols={metrics}
            size="sm"
            color="secondary"
            outline
            className="fs-export-income-statement text-muted"
          />
        )}
      </div>
      <ChartDiv>
        {msg ? (
          <Message>{msg}</Message>
        ) : (
          <YearlyMetricsChart
            data={data}
            metrics={chartMetrics}
            onMouseMove={onChartMouseMove}
            onMouseLeave={() => setHoveredData({})}
            showGridlines
            showTooltip={false}
            ticks={ticks}
          />
        )}
      </ChartDiv>
    </SummaryDiv>
  );
};

export default SelectableMetricChart;
